import apiClient from '../client';

// The hiring pipeline. Shapes mirror the backend contracts
// (CandidateSchema, HiringCheckpointSchema) — dates arrive as ISO strings.
//
// NOTE: the shared apiClient has a response interceptor that returns
// response.data, so every call here resolves to the response BODY.

// Six, not nine. Five of the old nine were one conversation holding one
// checkpoint each, and two of them split five checkpoints that have no
// order between them. See the backend's contracts/index.js.
export type CandidateStage =
  | 'lead' | 'talking' | 'clearing' | 'setup' | 'ready';

/** Which parallel track a checkpoint runs in, inside `clearing`. */
export type Track = 'vetting' | 'paperwork' | 'training';

/** Whose move a checkpoint is. A different question from whose job. */
export type OwedBy = 'us' | 'them' | 'third_party';

// Separate from the stage on purpose. The spreadsheet this replaces held
// both in one dropdown, so "Screening" and "Withdrew" were alternatives —
// and "withdrew during screening", the single most useful fact about a
// lost candidate, could not be written down at all.
export type CandidateOutcome =
  | 'open' | 'hired' | 'declined' | 'withdrew' | 'unresponsive' | 'no_show';

export type CandidateSource =
  | 'indeed' | 'referral' | 'walk_in' | 'facebook' | 'craigslist'
  | 'agency' | 'rehire' | 'other';

// 'field' is satisfied by a field on the candidate record, exactly as
// 'document' is satisfied by a file. Neither gets a tick box.
export type CheckpointKind = 'document' | 'value' | 'manual' | 'field';
export interface MessageTemplate {
  key: string;
  label: string;
  when: string;
  body: string;
  /** False once this company has reworded it — which is what "reset" undoes. */
  isDefault: boolean;
  defaultBody: string;
}

export interface InterviewGuide {
  body: string;
  isDefault: boolean;
  defaultBody: string;
  /** The three characters of convention the script uses. */
  syntax: Array<{ prefix: string; meaning: string }>;
}

export interface HiringSettings {
  defaultDayRate: number | null;
  /** The rest of the pay the interview quotes. */
  stopThreshold: number | null;
  overStopRate: number | null;
  trainingDayRate: number | null;
  /** Days of silence after the first message before a candidacy closes
   *  itself. 0 is off; the server fills in its default when nobody has
   *  chosen. */
  autoCloseDays: number;
  autoCloseDefault: number;
  /** When a new driver reports on day one, "HH:mm". */
  reportTime: string;
  /** The BCs a candidate can be handed to on day one, by name. */
  trainers: Array<{ accountId: string; name: string }>;
  templates: MessageTemplate[];
  interviewGuide: InterviewGuide;
  /** The {{names}} a body may use; anything else is refused on save. */
  placeholders: string[];
}

/** A step as the next-action engine describes it. */
export interface ActionStep {
  key: string;
  label: string;
  step: string | null;
  track: string | null;
  /** Whose move it is right now: ours, theirs, or a lab's. */
  owedBy?: 'us' | 'them' | 'third_party' | null;
  /** When this step itself is late — not when the person went quiet. */
  dueAt?: string | null;
  overdueDays?: number;
}

/**
 * The first thing still missing, in process order, whoever owes it —
 * stepping past anything that is with a third party while there is other
 * work in the same stage.
 */
export interface NextAction extends ActionStep {
  /** Days of other people's time stuck behind it. */
  daysBehind: number;
  /** What is out with a lab or First Advantage meanwhile. */
  waitingOn?: ActionStep[];
}

export interface HiringNotices {
  autoClosed: Array<{
    _id: string; num: number | null; name: string;
    stage: string; reason: string | null; closedAt: string;
  }>;
  closingSoon: Array<{
    _id: string; num: number | null; name: string;
    idleDays: number; closesAt: string;
    /** The step they owe us, so a warning can be acted on. */
    waitingFor: string; reason: string;
  }>;
}

export interface CandidateMessage {
  key: string;
  label: string;
  when: string;
  body: string;
  /** Placeholders the record could not answer, e.g. ["[clinic]"]. */
  gaps: string[];
  /** Whether this is a message that fits the candidate's situation now. */
  fits: boolean;
  why: string | null;
  /** The checklist step this message is the doing of, if any. */
  about: string | null;
}

export interface CandidateMessages {
  suggested: string | null;
  idleDays: number;
  messages: CandidateMessage[];
}

export type CheckpointStatus = 'not_started' | 'in_progress' | 'done' | 'not_applicable';

export const STAGE_ORDER: CandidateStage[] = [
  'lead', 'talking', 'clearing', 'setup', 'ready',
];

// Checks, then training, then paperwork — the owner's reading order:
// the checks decide IF, the training decides WHEN, the paperwork tags
// along.
export const TRACK_ORDER: Track[] = ['vetting', 'training', 'paperwork'];

export interface StageProgress {
  stage: CandidateStage;
  requiredTotal: number;
  requiredDone: number;
  optionalTotal: number;
  optionalDone: number;
  outstanding: string[];
  /** Whose move, from the first thing still outstanding in this stage. */
  owedBy?: OwedBy | null;
  complete: boolean;
}

export interface Candidate {
  _id: string;
  companyId: string;
  serviceAreaId?: number | null;
  /** The tracker's row number — per company, by arrival, never reused. */
  num?: number | null;
  firstName: string;
  lastName: string;
  fullName?: string;
  email?: string | null;
  phone?: string | null;
  stage: CandidateStage;
  stageSetManuallyAt?: string | null;
  outcome: CandidateOutcome;
  outcomeReason?: string;
  closedAt?: string | null;
  source: CandidateSource;
  sourceDetail?: string;
  ownerAccountId?: string | null;
  /** The BC who meets them on day one. Unset with one BC means that one. */
  trainerAccountId?: string | null;
  offeredPayRate?: number;
  offerApprovedByAccountId?: string | null;
  offerApprovedAt?: string | null;
  licenseState?: string;
  /** FedEx's own ID for this person — mirrored by the fedex_id checkpoint. */
  fedexId?: string;
  /** The password FedEx issues for this driver's account. */
  fedexPassword?: string;
  /** Somebody worth watching — a person's mark, never set automatically. */
  starred?: boolean;
  licenseExpiresAt?: string | null;
  mecExpiresAt?: string | null;
  // Back-office tier — absent entirely from a list response, and from a
  // detail response for anyone who is not an AO or CA.
  /** Full SSN — E-Verify needs it whole. Back-office tier; last four derived. */
  ssn?: string;
  /** The E-Verify case number — on the record, mirrored by its checkpoint. */
  everifyCase?: string;
  ssnLast4?: string;
  dateOfBirth?: string | null;
  address?: string;
  licenseNumber?: string;
  workAuthorized?: boolean;
  /** Decides whether the USCIS checkpoint applies. Not work authorisation. */
  usCitizen?: boolean;
  /** The A-number, when they are not a citizen. Back-office tier. */
  uscisNumber?: string;
  uniformSizes?: string;
  targetStartDate?: string | null;
  /** When they said they could talk. The only thing here with a time on it. */
  scheduledCallAt?: string | null;
  /** Questions covered on the call, by their own text. Not the answers. */
  interviewCovered?: string[];
  notes?: string;
  hiredAccountId?: string | null;
  hiredAt?: string | null;
  lastActivityAt?: string;
  createdAt?: string;
  // Added by the list endpoint.
  daysSinceActivity?: number | null;
  /** The current stage's meter — what is left in front of them right now. */
  progress?: StageProgress | null;
  /** The whole process, with a denominator that is the same for everybody. */
  overall?: { done: number; total: number };
  /** The next thing WE owe them — null when the ball is not in our court. */
  nextAction?: NextAction | null;
  /**
   * Every cell for the grid view, keyed by checkpoint — present only when
   * the list was asked with `checkpoints: true`. A key absent from the map
   * IS the not-applicable signal: the map is built from this candidate's
   * personal catalog, so `onlyWhen` rows that do not apply never appear.
   */
  checkpoints?: Record<string, {
    status: CheckpointStatus;
    state?: string | null;
    value?: string | null;
    /** Present only once finished — what a date column shows. */
    completedAt?: string | null;
    /** This step's own deadline, once it can actually be started. */
    dueAt?: string | null;
    dueDays?: number;
    overdueDays?: number;
    overdue?: boolean;
  }>;
}

export interface Checkpoint {
  key: string;
  label: string;
  /** How long this step may take once it can be started, and whether it
   *  has run over. Absent while it is blocked or its stage is ahead. */
  dueAt?: string | null;
  dueDays?: number;
  overdueDays?: number;
  overdue?: boolean;
  stage: CandidateStage;
  kind: CheckpointKind;
  documentTypeKey?: string;
  /** For kind 'field': the Candidate field that satisfies it. */
  candidateField?: string;
  valueLabel?: string;
  /** The value is recorded when given, never demanded — a pass with no
   *  date written down is still a pass. */
  valueOptional?: boolean;
  /** Which control the value wants — a date gets a date picker. */
  /** 'datetime' is a time somebody agreed to — see `interview_scheduled`. */
  valueType?: 'text' | 'date' | 'datetime';
  /** This step exists only until that one is finished. */
  untilDone?: string | null;
  required: boolean;
  owedBy?: OwedBy;
  /** Real gates only — stage order is NOT a dependency. */
  dependsOn?: string[];
  /** Shown only when a candidate field matches. */
  onlyWhen?: { field: string; equals: unknown };
  track?: Track;
  /** A waiting period that is a rule: the safety quiz gates the road test. */
  readyAfter?: { key: string; days: number; note?: string };
  /** One task passing through several hands, rather than several tasks. */
  states?: Array<{ key: string; label: string; owedBy: OwedBy }>;
  /** What finished is called, where it has a name — "Clear", not "Done". */
  doneLabel?: string;
  /** Which state it has reached, while in progress. */
  state?: string | null;
  /** Whose move it is right now — the state's owner, not the definition's. */
  owedByNow?: OwedBy | null;
  nextState?: { key: string; label: string; owedBy: OwedBy } | null;
  responsibleRole?: string;
  sortOrder: number;
  status: CheckpointStatus;
  value: string | null;
  attachmentId: string | null;
  completedAt: string | null;
  completedByAccountId: string | null;
  notes: string | null;
  finished: boolean;
}

export interface CandidateAttachment {
  _id: string;
  candidateId: string;
  documentTypeKey?: string | null;
  label?: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
  uploadedAt: string;
  promotedDocumentId?: string | null;
  file: { contentType?: string; sizeBytes?: number };
}

export interface BlockedReason {
  /** Unfinished checkpoint keys this one depends on. */
  waitingFor: string[];
  /** ISO date this becomes possible, for a time-based rule. */
  availableFrom: string | null;
  note: string | null;
}

export interface CandidateDetail {
  candidate: Candidate;
  catalog: Checkpoint[];
  checkpoints: Array<Partial<Checkpoint> & { key: string; status: CheckpointStatus }>;
  /** The one next thing — same engine as the to-do list. */
  nextAction?: NextAction | null;
  progress: StageProgress[];
  /** key → what is holding it up, and from when it becomes possible. */
  blocked?: Record<string, BlockedReason>;
  /** Everything still required anywhere, including behind their stage. */
  outstanding?: string[];
  attachments: CandidateAttachment[];
  daysSinceActivity: number | null;
}

export interface StalledCandidate extends Candidate {
  idleDays: number;
  threshold: number;
}

export interface SimilarResult {
  candidates: Array<Pick<Candidate, '_id' | 'firstName' | 'lastName' | 'email' | 'phone' | 'stage' | 'outcome'>>;
  accounts: Array<{
    accountId: string; name: string; email?: string; role: string; status: string; since?: string;
  }>;
}

export interface HirePreview {
  candidate: Candidate;
  blockers: string[];
  documentsToFile: string[];
  accountFields: string[];
  existingAccounts: SimilarResult['accounts'];
}

export interface HireResult {
  accountId: string;
  userId: string;
  isNewUser: boolean;
  invitationSent: boolean;
  promoted: Array<{ documentTypeKey: string; fileName: string }>;
  skipped: Array<{ attachmentId: string; reason: string }>;
  warnings: string[];
}

export interface Insights {
  window: { from: string; to: string };
  pipeline: Array<{
    stage: CandidateStage;
    count: number;
    medianIdleDays: number | null;
    oldestIdleDays: number | null;
    /** The stall sweep's limit for this stage; null where waiting is not stalling. */
    stallDays: number | null;
    stalled: number;
  }>;
  openTotal: number;
  entered: number;
  funnel: Array<{ stage: CandidateStage; count: number; shareOfEntered: number | null }>;
  outcomes: Partial<Record<CandidateOutcome, number>>;
  lostAtStage: Partial<Record<CandidateStage, number>>;
  sources: Array<{
    source: CandidateSource; entered: number; hired: number; lost: number; open: number;
    conversion: number | null;
  }>;
  hiredCount: number;
  medianDaysToHire: number | null;
  cohortIsSmall: boolean;
  lostCount: number;
  /** Hired, of everyone in the window who has finished. */
  conversion: number | null;
  lostBy: Partial<Record<CandidateStage, Partial<Record<CandidateOutcome, number>>>>;
  autoClosed: number;
  stalledTotal: number;
  stalled: InsightPerson[];
  blockers: Array<{
    key: string; label: string; stage: CandidateStage | null; count: number; overdue: number;
    owedBy: { us: number; them: number; third_party: number };
    people: InsightPerson[];
  }>;
  readyNoStartTotal: number;
  readyNoStart: InsightPerson[];
  unowned: number;
  trend: { unit: 'week' | 'month'; points: Array<{ start: string; added: number; hired: number; lost: number }> };
  previous: {
    from: string; to: string; entered: number; hiredCount: number;
    medianDaysToHire: number | null; conversion: number | null;
  };
}

/** Somebody behind a number on the insights page. */
export interface InsightPerson {
  _id: string;
  name: string;
  stage: CandidateStage;
  idleDays?: number;
  overBy?: number;
  overdueDays?: number;
  owedBy?: 'us' | 'them' | 'third_party';
}

export interface ImportRowResult {
  row: number;
  name: string;
  email: string;
  ok: boolean;
  code: string;
  message: string;
  stage?: CandidateStage;
  outcome?: CandidateOutcome;
}

export interface ImportSummary {
  total: number;
  ready: number;
  skipped: number;
  written?: number;
  byStage: Partial<Record<CandidateStage, number>>;
  byOutcome: Partial<Record<CandidateOutcome, number>>;
  ssnsDropped: number;
  backfilled: number;
  unbackedDocuments: number;
  matchedHires: string[];
  unmatchedHires: string[];
  mergedDuplicates: string[];
  sharedContacts: string[];
  passwordsDropped: number;
  missingColumns: Array<{ column: string; cost: string }>;
  dryRun: boolean;
}

export interface ImportResponse {
  results: ImportRowResult[];
  summary: ImportSummary;
}

export interface AuditEntry {
  _id: string;
  action: string;
  entityLabel?: string;
  actorName?: string;
  actorRole?: string;
  metadata?: Record<string, any>;
  at: string;
}

/**
 * Told after any write that can change who is in the pipeline or how a row
 * reads — so a view holding a copy (the sidebar queue) can refetch instead
 * of showing the list as it was when the page loaded.
 */
const writeListeners = new Set<() => void>();
export function onCandidatesWritten(fn: () => void): () => void {
  writeListeners.add(fn);
  return () => { writeListeners.delete(fn); };
}
function written<T>(request: Promise<T>): Promise<T> {
  return request.then((result) => {
    writeListeners.forEach((fn) => fn());
    return result;
  });
}

const HiringService = {
  async list(params: {
    stage?: string; outcome?: string; serviceAreaId?: number | string; source?: string;
    ownerAccountId?: string; search?: string; includeClosed?: boolean;
    page?: number; pageSize?: number;
    /** Ask for every cell — the grid view only; everyone else skips the weight. */
    checkpoints?: boolean;
  } = {}): Promise<{ items: Candidate[]; total: number }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
    });
    return apiClient.get(`/api/hiring/candidates?${query.toString()}`) as any;
  },

  async stalled(): Promise<{ items: StalledCandidate[]; total: number }> {
    return apiClient.get('/api/hiring/candidates/stalled') as any;
  },

  /** The company's checkpoint catalog as this role sees it — the grid's columns. */
  async catalog(): Promise<{ catalog: Checkpoint[] }> {
    return apiClient.get('/api/hiring/catalog') as any;
  },

  async get(id: string): Promise<CandidateDetail> {
    return apiClient.get(`/api/hiring/candidates/${id}`) as any;
  },

  async history(id: string): Promise<{ items: AuditEntry[]; total: number }> {
    return apiClient.get(`/api/hiring/candidates/${id}/history`) as any;
  },

  async create(input: Partial<Candidate>): Promise<{ candidate: Candidate; similar: SimilarResult }> {
    return written(apiClient.post('/api/hiring/candidates', input) as any);
  },

  async update(id: string, input: Partial<Candidate>): Promise<{ candidate: Candidate }> {
    return written(apiClient.patch(`/api/hiring/candidates/${id}`, input) as any);
  },

  async setStage(id: string, stage: CandidateStage, reason?: string): Promise<{ candidate: Candidate }> {
    return written(apiClient.post(`/api/hiring/candidates/${id}/stage`, { stage, reason }) as any);
  },

  async close(id: string, outcome: CandidateOutcome, reason?: string): Promise<{ candidate: Candidate }> {
    return written(apiClient.post(`/api/hiring/candidates/${id}/close`, { outcome, reason }) as any);
  },

  async reopen(id: string): Promise<{ candidate: Candidate }> {
    return written(apiClient.post(`/api/hiring/candidates/${id}/reopen`, {}) as any);
  },

  async remove(id: string): Promise<{ deleted: boolean }> {
    return written(apiClient.delete(`/api/hiring/candidates/${id}`) as any);
  },

  async similar(params: { email?: string; phone?: string; excludeId?: string }): Promise<SimilarResult> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return apiClient.get(`/api/hiring/candidates/similar?${query.toString()}`) as any;
  },

  async checkpoints(id: string): Promise<{ checkpoints: Checkpoint[] }> {
    return apiClient.get(`/api/hiring/candidates/${id}/checkpoints`) as any;
  },

  async setCheckpoint(id: string, key: string, body: {
    status: CheckpointStatus; state?: string | null;
    value?: string; attachmentId?: string; notes?: string;
    // `detail` is the whole redrawn page — the stage, the meter, the
    // next action and every deadline the tick moved — so a tick costs
    // one request instead of four.
  }): Promise<{
    stage: CandidateStage;
    checkpoint: Checkpoint;
    /** The checklist's own shape: definitions merged with their rows. */
    checkpoints?: Checkpoint[];
    detail?: CandidateDetail;
  }> {
    return written(apiClient.put(`/api/hiring/candidates/${id}/checkpoints/${key}`, body) as any);
  },

  async uploadAttachment(id: string, form: FormData): Promise<{ attachment: CandidateAttachment }> {
    return apiClient.post(`/api/hiring/candidates/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as any;
  },

  async deleteAttachment(attachmentId: string): Promise<{ deleted: boolean }> {
    return apiClient.delete(`/api/hiring/attachments/${attachmentId}`) as any;
  },

  /**
   * Fetches a candidate's file.
   *
   * Fetched, not linked. The stream endpoint sits behind the same bearer
   * token as everything else, and a plain <a href> sends no Authorization
   * header — which is why clicking a file used to land on "Cannot GET
   * //api/hiring/attachments/…/stream" instead of the file. (The doubled
   * slash came from the API base ending in one.)
   *
   * It comes back as a blob for the viewer to render. Handing the blob to
   * a new tab did not work either: a tab opened by script and pointed at
   * a blob: URL is blocked or lands blank in several browsers, and the
   * failure is silent — the file is just "not opening" again.
   */
  async attachmentBlob(attachmentId: string): Promise<Blob> {
    return apiClient.get(
      `/api/hiring/attachments/${attachmentId}/stream`,
      { responseType: 'blob' },
    ) as unknown as Blob;
  },

  async hirePreview(id: string): Promise<HirePreview> {
    return apiClient.get(`/api/hiring/candidates/${id}/hire`) as any;
  },

  async hire(id: string, body: {
    role?: string; serviceAreaId?: number | null; employeeId?: string;
    managerId?: string; startDate?: string; sendSms?: boolean;
  }): Promise<HireResult> {
    return written(apiClient.post(`/api/hiring/candidates/${id}/hire`, body) as any);
  },

  async link(id: string, accountId: string): Promise<{ accountId: string }> {
    return apiClient.post(`/api/hiring/candidates/${id}/link`, { accountId }) as any;
  },

  /**
   * Bring a spreadsheet in. Previews by default — `dryRun: false` has to be
   * asked for, so a forgotten flag can never write.
   */
  async import(rows: Record<string, string>[], dryRun = true): Promise<ImportResponse> {
    return dryRun
      ? apiClient.post('/api/hiring/import', { rows, dryRun }) as any
      : written(apiClient.post('/api/hiring/import', { rows, dryRun }) as any);
  },

  /** Company-wide hiring defaults: the day rate, and the message wording. */
  /** Who the silence rule closed since `since`, and who it is about to. */
  async notices(since?: string | null): Promise<HiringNotices> {
    const query = since ? `?since=${encodeURIComponent(since)}` : '';
    return apiClient.get(`/api/hiring/notices${query}`) as any;
  },

  async settings(): Promise<HiringSettings> {
    return apiClient.get('/api/hiring/settings') as any;
  },

  async saveSettings(body: Partial<{
    defaultDayRate: number | null;
    stopThreshold: number | null;
    overStopRate: number | null;
    trainingDayRate: number | null;
  }>): Promise<HiringSettings> {
    return apiClient.put('/api/hiring/settings', body) as any;
  },

  /** An empty body resets that message to the wording this product ships. */
  async saveTemplate(key: string, body: string): Promise<{ templates: MessageTemplate[] }> {
    return apiClient.put(`/api/hiring/settings/templates/${key}`, { body }) as any;
  },

  /** Same again for the interview script. Empty body = back to ours. */
  async saveGuide(body: string): Promise<{ interviewGuide: InterviewGuide }> {
    return apiClient.put('/api/hiring/settings/interview-guide', { body }) as any;
  },

  /** The script for the call, with their name and this company's pay in it. */
  async guide(id: string): Promise<{ body: string; gaps: string[] }> {
    return apiClient.get(`/api/hiring/candidates/${id}/interview-guide`) as any;
  },

  /**
   * The message to send this candidate, already written. `suggested` is
   * the key of the one that fits where they have got to; the rest are
   * there because the right message is sometimes the one the engine did
   * not pick.
   */
  async messages(id: string): Promise<CandidateMessages> {
    return apiClient.get(`/api/hiring/candidates/${id}/messages`) as any;
  },

  async insights(params: { from?: string; to?: string } = {}): Promise<Insights> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) query.set(k, String(v)); });
    return apiClient.get(`/api/hiring/insights?${query.toString()}`) as any;
  },
};

export default HiringService;
